import { ref, reactive, computed, watch } from 'vue'
import type { Classe, ClasseFormData } from '@/types/administration'
import { useApi } from './useApi'
import { toast } from 'vue3-toastify'

// Typage générique de la réponse API
interface ApiResponse<T> {
  success: boolean
  data: T
  message?: string
  errors?: Record<string, string[]>
  total?: number
  current_page?: number
  per_page?: number
}

interface ClasseState {
  classes: Classe[]
  classe: Classe | null
  loading: boolean
  errors: Record<string, string[]>
  filters: {
    search: string
    status: 'Tous' | 'Actifs' | 'Inactifs'
    niveau: string | null
    filiere_id: number | null
    page: number
    per_page: number
  }
  pagination: {
    total: number
    current_page: number
    per_page: number
  }
}

// Ajout du type pour le retour des fonctions du store
export type ClasseStoreResult = {
  success: boolean;
  message: any;
  isMaquetteActiveError?: boolean;
};

export function useClasses() {
  const { apiData } = useApi()
  const state = reactive<ClasseState>({
    classes: [],
    classe: null,
    loading: false,
    errors: {},
    filters: {
      search: '',
      status: 'Tous',
      niveau: null,
      filiere_id: null,
      page: 1,
      per_page: 50
    },
    pagination: {
      total: 0,
      current_page: 1,
      per_page: 50
    }
  })

  const formRef = ref()

  // 1. Enlève la colonne Options et ajoute Spécialités associées
  const headers = [
    { title: 'Code', align: 'start' as const, key: 'code', sortable: true },
    { title: 'Grade', align: 'start' as const, key: 'niveau', sortable: true },
    { title: 'Intitulé', align: 'start' as const, key: 'intitule', sortable: true },
    { title: 'Filière', align: 'start' as const, key: 'filiere.nom', sortable: true },
    { title: 'Spécialités associées', align: 'start' as const, key: 'specialites', sortable: false },
    { title: 'Statut', align: 'start' as const, key: 'is_active', sortable: true },
    { title: 'Actions', align: 'center' as const, key: 'actions', sortable: false }
  ]

  // 2. Supprime toute règle liée à options
  const rules = {
    niveau: [
      (v: string) => !!v || 'Le grade est requis',
      (v: string) => v.length <= 50 || 'Le grade ne doit pas dépasser 50 caractères'
    ],
    intitule: [
      (v: string) => !!v || "L'intitulé est requis",
      (v: string) => v.length <= 255 || "L'intitulé ne doit pas dépasser 255 caractères"
    ],
    filiere_id: [
      (v: number) => !!v || 'La filière est requise',
    ],
    semestre_possible: [
      (v: string) => !!v || 'Les semestres possibles sont requis',
      (v: string) => /^[0-9,\s]+$/.test(v) || 'Les semestres doivent être des chiffres séparés par des virgules',
      (v: string) => v.length <= 100 || 'La liste des semestres ne doit pas dépasser 100 caractères'
    ],
    duree_semestre: [
      (v: number) => !v || (v >= 1 && v <= 12) || 'La durée du semestre doit être entre 1 et 12 mois'
    ],
    code: [
      (v: string) => !v || v.length <= 20 || 'Le code ne doit pas dépasser 20 caractères'
    ]
  }

  const loadingState = ref(false)
  let searchTimeout: NodeJS.Timeout | null = null

  const filteredClasses = computed(() => {
    return state.classes.filter(classe => {
      const searchTerm = state.filters.search.toLowerCase().trim();
      const matchSearch = !searchTerm ||
        classe.code?.toLowerCase().includes(searchTerm) ||
        classe.niveau?.toLowerCase().includes(searchTerm) ||
        classe.intitule?.toLowerCase().includes(searchTerm);
      const matchStatus = state.filters.status === 'Tous' ||
        (state.filters.status === 'Actifs' && classe.is_active) ||
        (state.filters.status === 'Inactifs' && !classe.is_active)
      const matchNiveau = !state.filters.niveau || classe.niveau === state.filters.niveau
      const matchFiliere = !state.filters.filiere_id || classe.filiere_id === state.filters.filiere_id
      return matchSearch && matchStatus && matchNiveau && matchFiliere
    })
  })

  // Fonction utilitaire pour supprimer les accents
  function removeAccents(str: string): string {
    return str.normalize('NFD').replace(/\p{Diacritic}/gu, '');
  }

  // Fonction pour générer le code à partir du niveau (logique backend)
  function generateCodeFromNiveau(niveau: string): string {
    if (!niveau) return '';
    let clean = removeAccents(niveau.trim().toUpperCase());
    // Exemples : LICENCE 1 -> L1, MASTER 2 -> M2, CERTIFICATION -> C1
    if (clean.includes('LICENCE')) {
      const match = clean.match(/LICENCE\s*(\d*)/);
      const num = match && match[1] ? match[1] : '1';
      return 'L' + num;
    } else if (clean.includes('MASTER')) {
      const match = clean.match(/MASTER\s*(\d*)/);
      const num = match && match[1] ? match[1] : '1';
      return 'M' + num;
    } else if (clean.includes('CERTIFICATION')) {
      return 'C1';
    }
    // Sinon, prendre la première lettre de chaque mot (max 3 lettres)
    const words = clean.split(/\s+/);
    let code = '';
    for (const word of words) {
      if (word) code += word[0];
    }
    return code.substring(0, 3);
  }

  const classeForm = ref<ClasseFormData>({
    niveau: '',
    intitule: '',
    filiere_id: null as unknown as number,
    semestre_possible: '',
    duree_semestre: 5,
    is_active: true,
    code_auto_generated: true,
    code: '',
    specialite_ids: []
  });

  function openDialog(type: 'create' | 'edit' = 'create', item?: Classe) {
    if (type === 'edit' && item) {
      classeForm.value = {
        id: item.id,
        niveau: item.niveau,
        intitule: item.intitule,
        filiere_id: item.filiere_id,
        semestre_possible: item.semestre_possible || '',
        duree_semestre: item.duree_semestre || 5,
        is_active: item.is_active,
        code_auto_generated: !item.code || generateCodeFromNiveau(item.niveau) === item.code,
        code: item.code,
        specialite_ids: item.specialites ? item.specialites.map(s => s.id) : []
      };
    } else {
      classeForm.value = {
        niveau: '',
        intitule: '',
        filiere_id: null as unknown as number,
        semestre_possible: '',
        duree_semestre: 5,
        is_active: true,
        code_auto_generated: true,
        code: '',
        specialite_ids: []
      };
    }
    state.errors = {};
  }

  watch(
    () => classeForm.value.niveau,
    (newNiveau) => {
      if (classeForm.value.code_auto_generated) {
        classeForm.value.code = generateCodeFromNiveau(newNiveau || '');
      }
    }
  );

  watch(
    () => classeForm.value.code_auto_generated,
    (isAuto) => {
      if (isAuto) {
        classeForm.value.code = generateCodeFromNiveau(classeForm.value.niveau || '');
      }
    }
  );

  watch(
    () => classeForm.value.code,
    () => {
      if (state.errors.code) delete state.errors.code;
    }
  );

  watch(
    () => classeForm.value.code_auto_generated,
    () => {
      if (state.errors.code) delete state.errors.code;
    }
  );

  // Watcher pour formater automatiquement le champ semestre_possible
  watch(
    () => classeForm.value.semestre_possible,
    (newValue) => {
      if (newValue) {
        // Formater : enlever les espaces et garder seulement les chiffres et virgules
        const formatted = newValue
          .replace(/\s+/g, '') // Enlever tous les espaces
          .replace(/[^0-9,]/g, '') // Garder seulement les chiffres et virgules
          .replace(/,+/g, ',') // Remplacer les virgules multiples par une seule
          .replace(/^,|,$/g, ''); // Enlever les virgules au début et à la fin
        
        // Mettre à jour la valeur formatée si elle a changé
        if (formatted !== newValue) {
          classeForm.value.semestre_possible = formatted;
        }
      }
    }
  );

  async function fetchClasses() {
    try {
      loadingState.value = true
      const params = new URLSearchParams()
      if (state.filters.search) params.append('search', state.filters.search)
      if (state.filters.status !== 'Tous') params.append('is_active', (state.filters.status === 'Actifs').toString())
      if (state.filters.niveau) params.append('niveau', state.filters.niveau)
      if (state.filters.filiere_id) params.append('filiere_id', state.filters.filiere_id.toString())
      // Charger toutes les entrées pour paginer côté client et garder une UX fluide
      params.append('per_page', 'all')
      const res = await apiData<ApiResponse<Classe[]>>(`/classes?${params.toString()}`)
      if (res?.success) {
        if (Array.isArray(res.data)) {
          state.classes = res.data
          state.pagination.total = res.data.length
          state.pagination.current_page = 1
          state.pagination.per_page = state.filters.per_page
        } else if (Array.isArray((res.data as any)?.data)) {
          const list = (res.data as any).data
          state.classes = list
          state.pagination.total = list.length
          state.pagination.current_page = 1
          state.pagination.per_page = state.filters.per_page
        } else {
          state.classes = []
          state.pagination.total = 0
          state.pagination.current_page = 1
          state.pagination.per_page = state.filters.per_page
        }
      } else {
        state.classes = []
        state.pagination.total = 0
        state.pagination.current_page = 1
        state.pagination.per_page = state.filters.per_page
      }
    } catch (e) {
      state.classes = []
      state.pagination.total = 0
      state.pagination.current_page = 1
      state.pagination.per_page = state.filters.per_page
    } finally {
      loadingState.value = false
    }
  }

  // Watchers ciblés: pas de refetch sur page/per_page, debounce sur recherche
  watch(
    () => state.filters.search,
    () => {
      if (searchTimeout) clearTimeout(searchTimeout)
      searchTimeout = setTimeout(() => {
        state.filters.page = 1
        fetchClasses()
      }, 300)
    }
  )

  watch(
    () => state.filters.status,
    () => {
      state.filters.page = 1
      fetchClasses()
    }
  )

  watch(
    () => state.filters.niveau,
    () => {
      state.filters.page = 1
      fetchClasses()
    }
  )

  watch(
    () => state.filters.filiere_id,
    () => {
      state.filters.page = 1
      fetchClasses()
    }
  )

  async function createClasse(data: ClasseFormData): Promise<ClasseStoreResult> {
    try {
      const payload = {
        niveau: data.niveau,
        intitule: data.intitule,
        filiere_id: data.filiere_id,
        semestre_possible: data.semestre_possible || undefined,
        duree_semestre: data.duree_semestre || undefined,
        is_active: data.is_active,
        code: data.code_auto_generated ? generateCodeFromNiveau(data.niveau || '') : (data.code || ''),
        specialite_ids: data.specialite_ids && data.specialite_ids.length > 0 ? data.specialite_ids : undefined
      };
      const res = await apiData<ApiResponse<Classe>>('/classes', { method: 'POST', body: JSON.stringify(payload) })
      if (res.success) {
        await fetchClasses()
        return { success: true, message: res.message || 'Classe créée avec succès' }
      }
      if (res.errors && typeof res.errors === 'object') {
        state.errors = res.errors
        const messages = Object.values(res.errors).flat().join('\n')
        if (messages.includes('maquette active existe')) {
          toast.error(messages)
          await fetchClasses()
          resetForm()
          return { success: false, message: messages, isMaquetteActiveError: true }
        }
        return { success: false, message: messages || res.message || 'Erreur de validation' }
      }
      if (res.message && res.message.includes('maquette active existe')) {
        toast.error(res.message)
        await fetchClasses()
        resetForm()
        return { success: false, message: res.message, isMaquetteActiveError: true }
      }
      return { success: false, message: res.message || 'Une erreur s\'est produite' }
    } catch (err: any) {
      const backendMsg = err?.response?._data?.message || err?.data?.message
      if (backendMsg && backendMsg.includes('maquette active existe')) {
        toast.error(backendMsg)
        await fetchClasses()
        resetForm()
        return { success: false, message: backendMsg, isMaquetteActiveError: true }
      }
      if (backendMsg) return { success: false, message: backendMsg }
      if (err?.response?._data?.errors) {
        state.errors = err.response._data.errors
        const messages = Object.values(err.response._data.errors).flat().join('\n')
        return { success: false, message: messages }
      }
      return { success: false, message: 'Erreur inconnue' }
    }
  }

  async function updateClasse(id: number, data: ClasseFormData): Promise<ClasseStoreResult> {
    try {
      const payload = {
        niveau: data.niveau,
        intitule: data.intitule,
        filiere_id: data.filiere_id,
        semestre_possible: data.semestre_possible || undefined,
        duree_semestre: data.duree_semestre || undefined,
        is_active: data.is_active,
        code: data.code_auto_generated ? generateCodeFromNiveau(data.niveau || '') : (data.code || ''),
        specialite_ids: data.specialite_ids && data.specialite_ids.length > 0 ? data.specialite_ids : undefined
      };
      const res = await apiData<ApiResponse<Classe>>(`/classes/${id}`, { method: 'PUT', body: JSON.stringify(payload) })
      if (res.success) {
        await fetchClasses()
        return { success: true, message: res.message || 'Classe modifiée avec succès' }
      }
      if (res.errors && typeof res.errors === 'object') {
        state.errors = res.errors
        const messages = Object.values(res.errors).flat().join('\n')
        if (messages.includes('maquette active existe')) {
          toast.error(messages)
          await fetchClasses()
          resetForm()
          return { success: false, message: messages, isMaquetteActiveError: true }
        }
        return { success: false, message: messages || res.message || 'Erreur de validation' }
      }
      if (res.message && res.message.includes('maquette active existe')) {
        toast.error(res.message)
        await fetchClasses()
        resetForm()
        return { success: false, message: res.message, isMaquetteActiveError: true }
      }
      return { success: false, message: res.message || 'Une erreur s\'est produite' }
    } catch (err: any) {
      const backendMsg = err?.response?._data?.message || err?.data?.message
      if (backendMsg && backendMsg.includes('maquette active existe')) {
        toast.error(backendMsg)
        await fetchClasses()
        resetForm()
        return { success: false, message: backendMsg, isMaquetteActiveError: true }
      }
      if (backendMsg) return { success: false, message: backendMsg }
      if (err?.response?._data?.errors) {
        state.errors = err.response._data.errors
        const messages = Object.values(err.response._data.errors).flat().join('\n')
        return { success: false, message: messages }
      }
      return { success: false, message: 'Erreur inconnue' }
    }
  }

  async function deleteClasse(id: number) {
    try {
      const res = await apiData<ApiResponse<null>>(`/classes/${id}`, { method: 'DELETE' })
      if (res.success) {
        await fetchClasses()
        return { success: true, message: res.message || 'Classe supprimée avec succès' }
      }
      return { success: false, message: res.message || 'Une erreur s\'est produite lors de la suppression' }
    } catch (err: any) {
      const backendMsg = err?.response?._data?.message || err?.data?.message
      if (backendMsg) return { success: false, message: backendMsg }
      return { success: false, message: 'Erreur inconnue' }
    }
  }

  async function toggleActive(id: number) {
    try {
      const res = await apiData<ApiResponse<Classe>>(`/classes/${id}/toggle-active`, { method: 'PATCH' })
      if (res.success) {
        await fetchClasses()
        return { success: true, message: res.message || 'Statut modifié avec succès' }
      }
      return { success: false, message: res.message || 'Erreur lors de la modification du statut' }
    } catch (err: any) {
      const backendMsg = err?.response?._data?.message || err?.data?.message
      if (backendMsg) return { success: false, message: backendMsg }
      return { success: false, message: 'Erreur inconnue' }
    }
  }

  function resetForm() {
    if (formRef.value) {
      formRef.value.reset()
    }
    // Réinitialiser aussi les nouveaux champs
    classeForm.value.semestre_possible = ''
    classeForm.value.duree_semestre = 5
    state.errors = {}
  }

  function errorsClear() {
    state.errors = {}
  }

  return {
    classes: filteredClasses,
    classe: state.classe,
    classeForm,
    loading: loadingState,
    errors: state.errors,
    filters: state.filters,
    pagination: state.pagination,
    formRef,
    headers,
    rules,
    fetchClasses,
    createClasse,
    updateClasse,
    deleteClasse,
    toggleActive,
    resetForm,
    errorsClear,
    generateCodeFromNiveau,
    openDialog
  }
} 